import { useEffect, useState } from "react";
import {
  isSupabaseConfigured,
  partnersRepo,
  productsRepo,
  shipperProfilesRepo,
  type NewPartner,
  type NewProduct,
  type NewShipperProfile,
} from "@/lib/db";
import type { Partner, PartnerRole, Product, ShipperProfile } from "@/types";
import { ConfigBanner } from "@/components/ConfigBanner";
import {
  extractDocument,
  extractErrMsg,
  type BizCertFields,
  type ExtractKind,
  type ProductFields,
} from "@/lib/extract-client";

type Tab = "partners" | "products" | "shipper";

const PARTNER_ROLES: PartnerRole[] = [
  "buyer",
  "consignee",
  "notify",
  "distributor",
  "importer_of_record",
  "fda_initial_importer",
  "us_agent",
  "three_pl",
  "us_subsidiary",
];

/**
 * Entrance ② master data — 거래처(partners) · 제품(products) · 자사(shipper).
 * List + create/update forms over @/lib/db repos. Guarded by isSupabaseConfigured().
 */
export function MasterDataPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>("partners");
  return (
    <div>
      <ConfigBanner />
      <section className="panel">
        <h2>마스터데이터</h2>
        <p className="sub">
          거래처·제품·자사정보를 한 번 입력하면 딜에서 재사용합니다(재입력 0).
        </p>
        <div className="tabs">
          <button
            className={tab === "partners" ? "tab active" : "tab"}
            onClick={() => setTab("partners")}
          >
            거래처
          </button>
          <button
            className={tab === "products" ? "tab active" : "tab"}
            onClick={() => setTab("products")}
          >
            제품
          </button>
          <button
            className={tab === "shipper" ? "tab active" : "tab"}
            onClick={() => setTab("shipper")}
          >
            자사정보
          </button>
        </div>

        {!isSupabaseConfigured() && (
          <p className="muted">
            Supabase 미설정 상태입니다. 환경변수를 채우면 마스터데이터를 입력할 수
            있습니다.
          </p>
        )}

        {isSupabaseConfigured() && tab === "partners" && <PartnersTab />}
        {isSupabaseConfigured() && tab === "products" && <ProductsTab />}
        {isSupabaseConfigured() && tab === "shipper" && <ShipperTab />}
      </section>
    </div>
  );
}

// --- partners ----------------------------------------------------------------

const EMPTY_PARTNER: NewPartner = {
  company_name: "",
  company_name_local: null,
  country: "US",
  address: null,
  contact_person: null,
  email: null,
  phone: null,
  tax_vat_id: null,
  eori: null,
  roles: [],
  is_importer_of_record: false,
  is_fda_initial_importer: false,
  fda_initial_importer_fei: null,
  us_physical_address: null,
  grade: null,
  importer_ids: {},
  sanction_check_status: "not_checked",
  sanction_checked_at: null,
};

function PartnersTab(): JSX.Element {
  const [rows, setRows] = useState<Partner[]>([]);
  const [form, setForm] = useState<NewPartner>(EMPTY_PARTNER);
  const [editId, setEditId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reload() {
    partnersRepo
      .list()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(reload, []);

  function toggleRole(role: PartnerRole) {
    setForm((f) => {
      const has = f.roles.includes(role);
      return {
        ...f,
        roles: has ? f.roles.filter((r) => r !== role) : [...f.roles, role],
      };
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload: NewPartner = {
        ...form,
        is_importer_of_record: form.roles.includes("importer_of_record"),
        is_fda_initial_importer: form.roles.includes("fda_initial_importer"),
      };
      if (editId) await partnersRepo.update(editId, payload);
      else await partnersRepo.create(payload);
      setForm(EMPTY_PARTNER);
      setEditId(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function edit(p: Partner) {
    const { id: _id, created_at: _c, ...rest } = p;
    void _id;
    void _c;
    setForm(rest);
    setEditId(p.id);
  }

  return (
    <div>
      {error && <p className="notice-error">{error}</p>}
      <table className="findings">
        <thead>
          <tr>
            <th>회사명</th>
            <th>국가</th>
            <th>역할</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                등록된 거래처가 없습니다.
              </td>
            </tr>
          )}
          {rows.map((p) => (
            <tr key={p.id}>
              <td>{p.company_name}</td>
              <td>{p.country}</td>
              <td className="muted">{p.roles.join(", ") || "—"}</td>
              <td>
                <button className="link-btn" onClick={() => edit(p)}>
                  수정
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="section-title">{editId ? "거래처 수정" : "거래처 추가"}</h3>
      <DocUpload
        kind="bizcert"
        hint="사업자등록증(이미지/PDF)을 올리면 회사명·주소·사업자번호·대표자를 자동 채웁니다. 국가는 직접 선택하세요."
        onFields={(f) => {
          const b = f as BizCertFields;
          setForm((cur) => ({
            ...cur,
            company_name: fillIfEmpty(cur.company_name, b.company_name) ?? "",
            address: fillIfEmpty(cur.address, b.address),
            tax_vat_id: fillIfEmpty(cur.tax_vat_id, b.biz_no),
            contact_person: fillIfEmpty(cur.contact_person, b.ceo_name),
          }));
        }}
      />
      <div className="form-grid">
        <Field label="회사명(영문) *">
          <input
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
        </Field>
        <Field label="국가(ISO2) *">
          <input
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
          />
        </Field>
        <Field label="주소">
          <input
            value={form.address ?? ""}
            onChange={(e) =>
              setForm({ ...form, address: e.target.value || null })
            }
          />
        </Field>
        <Field label="담당자">
          <input
            value={form.contact_person ?? ""}
            onChange={(e) =>
              setForm({ ...form, contact_person: e.target.value || null })
            }
          />
        </Field>
        <Field label="이메일">
          <input
            value={form.email ?? ""}
            onChange={(e) =>
              setForm({ ...form, email: e.target.value || null })
            }
          />
        </Field>
        <Field label="Tax/VAT ID">
          <input
            value={form.tax_vat_id ?? ""}
            onChange={(e) =>
              setForm({ ...form, tax_vat_id: e.target.value || null })
            }
          />
        </Field>
        <Field label="미국 내 실제 주소(Initial Importer 요건)">
          <input
            value={form.us_physical_address ?? ""}
            onChange={(e) =>
              setForm({ ...form, us_physical_address: e.target.value || null })
            }
          />
        </Field>
      </div>
      <div className="roles-box">
        <span className="roles-label">역할(다중 선택):</span>
        {PARTNER_ROLES.map((r) => (
          <label key={r} className="role-chip">
            <input
              type="checkbox"
              checked={form.roles.includes(r)}
              onChange={() => toggleRole(r)}
            />{" "}
            {r}
          </label>
        ))}
      </div>
      <div className="form-actions">
        <button onClick={save} disabled={busy || !form.company_name.trim()}>
          {busy ? "저장 중…" : editId ? "수정 저장" : "추가"}
        </button>
        {editId && (
          <button
            className="secondary"
            onClick={() => {
              setForm(EMPTY_PARTNER);
              setEditId(null);
            }}
          >
            취소
          </button>
        )}
      </div>
    </div>
  );
}

// --- products ----------------------------------------------------------------

const EMPTY_PRODUCT: NewProduct = {
  model_name: "",
  internal_code: null,
  description_en: "",
  hs_code: null,
  hs_verified: false,
  country_of_origin: "KR",
  unit_price: null,
  currency: "USD",
  net_weight_kg: null,
  gross_weight_kg: null,
  box_length_cm: null,
  box_width_cm: null,
  box_height_cm: null,
  cbm: null,
  device_class: null,
  regulatory: {},
  reg_verified_at: null,
  is_active: true,
};

function ProductsTab(): JSX.Element {
  const [rows, setRows] = useState<Product[]>([]);
  const [form, setForm] = useState<NewProduct>(EMPTY_PRODUCT);
  const [editId, setEditId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reload() {
    productsRepo
      .list()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(reload, []);

  function numOrNull(v: string): number | null {
    if (v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (editId) await productsRepo.update(editId, form);
      else await productsRepo.create(form);
      setForm(EMPTY_PRODUCT);
      setEditId(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function edit(p: Product) {
    const { id: _id, created_at: _c, ...rest } = p;
    void _id;
    void _c;
    setForm(rest);
    setEditId(p.id);
  }

  return (
    <div>
      {error && <p className="notice-error">{error}</p>}
      <table className="findings">
        <thead>
          <tr>
            <th>모델명</th>
            <th>단가</th>
            <th>HS</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                등록된 제품이 없습니다.
              </td>
            </tr>
          )}
          {rows.map((p) => (
            <tr key={p.id}>
              <td>{p.model_name}</td>
              <td>
                {p.unit_price ?? "—"} {p.currency}
              </td>
              <td className="muted">{p.hs_code ?? "미확인"}</td>
              <td>
                <button className="link-btn" onClick={() => edit(p)}>
                  수정
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="section-title">{editId ? "제품 수정" : "제품 추가"}</h3>
      <DocUpload
        kind="product"
        hint="FDA 510(k) 레터·등록증·제품 스펙시트(이미지/PDF)를 올리면 모델명·영문품명·등급·HS·FDA 식별자·중량을 자동 채웁니다."
        onFields={(f) => {
          const p = f as ProductFields;
          setForm((cur) => {
            const reg = { ...cur.regulatory };
            if (!String(reg.fda_510k_number ?? "").trim() && p.fda_510k_number)
              reg.fda_510k_number = p.fda_510k_number;
            if (!String(reg.fda_fei ?? "").trim() && p.fda_fei)
              reg.fda_fei = p.fda_fei;
            if (
              !String(reg.fda_device_listing ?? "").trim() &&
              p.fda_device_listing
            )
              reg.fda_device_listing = p.fda_device_listing;
            if (
              !String(reg.fda_510k_intended_use ?? "").trim() &&
              p.intended_use
            )
              reg.fda_510k_intended_use = p.intended_use;
            return {
              ...cur,
              model_name: fillIfEmpty(cur.model_name, p.model_name) ?? "",
              description_en:
                fillIfEmpty(cur.description_en, p.description_en) ?? "",
              hs_code: fillIfEmpty(cur.hs_code, p.hs_code),
              device_class: fillIfEmpty(cur.device_class, p.device_class),
              net_weight_kg: fillNumIfEmpty(cur.net_weight_kg, p.net_weight_kg),
              gross_weight_kg: fillNumIfEmpty(
                cur.gross_weight_kg,
                p.gross_weight_kg,
              ),
              regulatory: reg,
            };
          });
        }}
      />
      <div className="form-grid">
        <Field label="모델명 *">
          <input
            value={form.model_name}
            onChange={(e) => setForm({ ...form, model_name: e.target.value })}
          />
        </Field>
        <Field label="영문 품명(인보이스용) *">
          <input
            value={form.description_en}
            onChange={(e) =>
              setForm({ ...form, description_en: e.target.value })
            }
          />
        </Field>
        <Field label="HS 코드">
          <input
            value={form.hs_code ?? ""}
            onChange={(e) =>
              setForm({ ...form, hs_code: e.target.value || null })
            }
          />
        </Field>
        <Field label="원산지(ISO2)">
          <input
            value={form.country_of_origin}
            onChange={(e) =>
              setForm({ ...form, country_of_origin: e.target.value })
            }
          />
        </Field>
        <Field label="단가">
          <input
            type="number"
            value={form.unit_price ?? ""}
            onChange={(e) =>
              setForm({ ...form, unit_price: numOrNull(e.target.value) })
            }
          />
        </Field>
        <Field label="통화">
          <input
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          />
        </Field>
        <Field label="순중량(kg)">
          <input
            type="number"
            value={form.net_weight_kg ?? ""}
            onChange={(e) =>
              setForm({ ...form, net_weight_kg: numOrNull(e.target.value) })
            }
          />
        </Field>
        <Field label="총중량(kg)">
          <input
            type="number"
            value={form.gross_weight_kg ?? ""}
            onChange={(e) =>
              setForm({ ...form, gross_weight_kg: numOrNull(e.target.value) })
            }
          />
        </Field>
        <Field label="CBM">
          <input
            type="number"
            value={form.cbm ?? ""}
            onChange={(e) =>
              setForm({ ...form, cbm: numOrNull(e.target.value) })
            }
          />
        </Field>
        <Field label="등급(device class)">
          <input
            value={form.device_class ?? ""}
            onChange={(e) =>
              setForm({ ...form, device_class: e.target.value || null })
            }
          />
        </Field>
      </div>
      <div className="form-actions">
        <button
          onClick={save}
          disabled={
            busy || !form.model_name.trim() || !form.description_en.trim()
          }
        >
          {busy ? "저장 중…" : editId ? "수정 저장" : "추가"}
        </button>
        {editId && (
          <button
            className="secondary"
            onClick={() => {
              setForm(EMPTY_PRODUCT);
              setEditId(null);
            }}
          >
            취소
          </button>
        )}
      </div>
    </div>
  );
}

// --- shipper -----------------------------------------------------------------

const EMPTY_SHIPPER: NewShipperProfile = {
  company_name: "",
  address: null,
  business_reg_no: null,
  beneficiary_bank: null,
  signature_image_ref: null,
  logo_image_ref: null,
  deal_no_pattern: "BM-YYMMDD###",
  default_export_port: null,
};

function ShipperTab(): JSX.Element {
  const [rows, setRows] = useState<ShipperProfile[]>([]);
  const [form, setForm] = useState<NewShipperProfile>(EMPTY_SHIPPER);
  const [editId, setEditId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // local bank fields (folded into beneficiary_bank jsonb on save)
  const [bankName, setBankName] = useState("");
  const [bankSwift, setBankSwift] = useState("");
  const [bankAccount, setBankAccount] = useState("");

  function reload() {
    shipperProfilesRepo
      .list()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(reload, []);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const beneficiary_bank =
        bankName || bankSwift || bankAccount
          ? {
              bank_name: bankName,
              swift: bankSwift,
              account_no: bankAccount,
              currency: "USD",
            }
          : null;
      const payload: NewShipperProfile = { ...form, beneficiary_bank };
      if (editId) await shipperProfilesRepo.update(editId, payload);
      else await shipperProfilesRepo.create(payload);
      resetForm();
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function resetForm() {
    setForm(EMPTY_SHIPPER);
    setEditId(null);
    setBankName("");
    setBankSwift("");
    setBankAccount("");
  }

  function edit(s: ShipperProfile) {
    const { id: _id, created_at: _c, ...rest } = s;
    void _id;
    void _c;
    setForm(rest);
    setEditId(s.id);
    setBankName(s.beneficiary_bank?.bank_name ?? "");
    setBankSwift(s.beneficiary_bank?.swift ?? "");
    setBankAccount(s.beneficiary_bank?.account_no ?? "");
  }

  return (
    <div>
      {error && <p className="notice-error">{error}</p>}
      <table className="findings">
        <thead>
          <tr>
            <th>회사명</th>
            <th>기본 적재항</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="muted">
                등록된 자사정보가 없습니다.
              </td>
            </tr>
          )}
          {rows.map((s) => (
            <tr key={s.id}>
              <td>{s.company_name}</td>
              <td className="muted">{s.default_export_port ?? "—"}</td>
              <td>
                <button className="link-btn" onClick={() => edit(s)}>
                  수정
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="section-title">
        {editId ? "자사정보 수정" : "자사정보 추가"}
      </h3>
      <DocUpload
        kind="bizcert"
        hint="자사 사업자등록증(이미지/PDF)을 올리면 회사명·주소·사업자번호를 자동 채웁니다."
        onFields={(f) => {
          const b = f as BizCertFields;
          setForm((cur) => ({
            ...cur,
            company_name: fillIfEmpty(cur.company_name, b.company_name) ?? "",
            address: fillIfEmpty(cur.address, b.address),
            business_reg_no: fillIfEmpty(cur.business_reg_no, b.biz_no),
          }));
        }}
      />
      <div className="form-grid">
        <Field label="회사명 *">
          <input
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
        </Field>
        <Field label="주소">
          <input
            value={form.address ?? ""}
            onChange={(e) =>
              setForm({ ...form, address: e.target.value || null })
            }
          />
        </Field>
        <Field label="사업자번호">
          <input
            value={form.business_reg_no ?? ""}
            onChange={(e) =>
              setForm({ ...form, business_reg_no: e.target.value || null })
            }
          />
        </Field>
        <Field label="기본 적재항/공항">
          <input
            value={form.default_export_port ?? ""}
            onChange={(e) =>
              setForm({ ...form, default_export_port: e.target.value || null })
            }
          />
        </Field>
        <Field label="채번 규칙">
          <input
            value={form.deal_no_pattern}
            onChange={(e) =>
              setForm({ ...form, deal_no_pattern: e.target.value })
            }
          />
        </Field>
        <Field label="은행명(Beneficiary)">
          <input value={bankName} onChange={(e) => setBankName(e.target.value)} />
        </Field>
        <Field label="SWIFT">
          <input
            value={bankSwift}
            onChange={(e) => setBankSwift(e.target.value)}
          />
        </Field>
        <Field label="계좌번호">
          <input
            value={bankAccount}
            onChange={(e) => setBankAccount(e.target.value)}
          />
        </Field>
      </div>
      <div className="form-actions">
        <button onClick={save} disabled={busy || !form.company_name.trim()}>
          {busy ? "저장 중…" : editId ? "수정 저장" : "추가"}
        </button>
        {editId && (
          <button className="secondary" onClick={resetForm}>
            취소
          </button>
        )}
      </div>
    </div>
  );
}

// --- shared field wrapper ----------------------------------------------------

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

// --- document upload → AI auto-fill -----------------------------------------

/** Only fill a field when the current value is empty (never overwrite input). */
export function fillIfEmpty(
  cur: string | null | undefined,
  next: string,
): string | null {
  const has = String(cur ?? "").trim().length > 0;
  if (has) return (cur ?? null) as string | null;
  return next.trim() ? next.trim() : (cur ?? null);
}

/** Like fillIfEmpty but for numeric fields (kg / weights). */
export function fillNumIfEmpty(
  cur: number | null,
  next: number | null,
): number | null {
  if (cur !== null && cur !== undefined) return cur;
  return next;
}

/**
 * "📄 서류 올려 자동입력" — uploads an image/PDF, runs Gemini extraction, and
 * hands the extracted fields to `onFields`. The parent merges them into its
 * form (empty fields only — user reviews then saves manually; no auto-commit).
 */
function DocUpload({
  kind,
  hint,
  onFields,
}: {
  kind: ExtractKind;
  hint: string;
  onFields: (fields: BizCertFields | ProductFields) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onFile(file: File | undefined) {
    setError(null);
    setDone(false);
    if (!file) return;
    setBusy(true);
    try {
      const res = await extractDocument(file, kind);
      if (!res.ok || !res.fields) {
        setError(extractErrMsg(res.error));
        return;
      }
      onFields(res.fields);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="doc-upload">
      <label className="field">
        <span className="field-label">📄 서류 올려 자동입력</span>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,application/pdf"
          disabled={busy}
          onChange={(e) => {
            void onFile(e.target.files?.[0]);
            e.target.value = ""; // allow re-selecting the same file
          }}
        />
      </label>
      <p className="muted doc-hint">{hint}</p>
      {busy && <p className="notice-info">서류를 읽는 중입니다… 잠시만요.</p>}
      {done && !error && (
        <p className="notice-info">
          ✅ AI가 추출했습니다. 빈 칸만 채웠으니 <b>확인 후 저장</b>하세요.
        </p>
      )}
      {error && (
        <p className="notice-error">
          자동입력 실패: {error} 직접 입력해 주세요.
        </p>
      )}
    </div>
  );
}
